import { Controller } from "@hotwired/stimulus";
import { i18n } from "../lib/i18n";

export default class extends Controller {
  static targets = [
    "shelterFields",
    "facilitySelection",
    "householdInput",
    "householdInfo",
    "householdMembers",
    "headOfHouseholdCheck",
    "headOfHouseholdInfo",
    "autoExitInfo",
  ];
  static values = {
    shelterPrograms: Array,
    shelterFacilities: Object
  };

  declare readonly shelterFieldsTarget: HTMLElement;
  declare readonly facilitySelectionTarget: HTMLElement;
  declare readonly householdInputTarget: HTMLInputElement;
  declare readonly householdInfoTarget: HTMLElement;
  declare readonly householdMembersTarget: HTMLElement;
  declare readonly headOfHouseholdCheckTarget: HTMLInputElement;
  declare readonly headOfHouseholdInfoTarget: HTMLElement;
  declare readonly autoExitInfoTarget: HTMLElement;
  declare readonly shelterProgramsValue: number[];
  declare readonly shelterFacilitiesValue: Record<string, any[]>;

  programChanged(event: Event) {
    const select = event.target as HTMLSelectElement;
    const programId = parseInt(select.value);

    if (this.shelterProgramsValue.includes(programId)) {
      this.showShelterFields();
      this.loadFacilitiesForProgram(programId);
    } else {
      this.hideShelterFields();
    }
  }

  private loadFacilitiesForProgram(programId: number) {
    const facilities = this.shelterFacilitiesValue[programId.toString()] || [];

    if (facilities.length > 1) {
      // Multiple facilities - show selection
      this.showFacilitySelection(facilities);
    } else if (facilities.length === 1) {
      // Single facility - show info
      this.showSingleFacilityInfo(facilities[0]);
    } else {
      // No facilities - show message
      this.showNoFacilitiesMessage();
    }
  }

  private showFacilitySelection(facilities: any[]) {
    const facilityOptions = facilities
      .map(
        (facility) =>
          `<option value="${facility.id}">${facility.name} (${facility.facility_type}, ${i18n.t('program_selector.capacity_count', { count: facility.capacity })})</option>`,
      )
      .join("");

    this.facilitySelectionTarget.innerHTML = `
      <div>
        <label class="label">${i18n.t('program_selector.select_facility')}</label>
        <select name="enrollment[preferred_facility_id]" class="select select-bordered w-full">
          <option value="">${i18n.t('program_selector.choose_facility')}</option>
          ${facilityOptions}
        </select>
        <p class="mt-1 text-sm text-gray-500">
          ${i18n.t('program_selector.multiple_facilities_hint')}
        </p>
      </div>
    `;
  }

  private showSingleFacilityInfo(facility: any) {
    this.facilitySelectionTarget.innerHTML = `
      <div class="alert alert-info alert-sm">
        <div class="text-sm">
          <div class="font-medium">${i18n.t('program_selector.facility_name', { name: facility.name })}</div>
          <div class="mt-1">
            ${i18n.t('program_selector.facility_info', { type: facility.facility_type, capacity: facility.capacity })}
          </div>
        </div>
      </div>
      <input type="hidden" name="enrollment[preferred_facility_id]" value="${facility.id}">
    `;

    // Show auto-exit info for Night-by-Night facilities
    if (
      facility.facility_type === "Night-by-Night" &&
      facility.auto_exit_days
    ) {
      this.showAutoExitInfo(facility.auto_exit_days);
    } else {
      this.hideAutoExitInfo();
    }
  }

  private showNoFacilitiesMessage() {
    this.facilitySelectionTarget.innerHTML = `
      <div class="alert alert-warning alert-sm">
        <div class="text-sm">
          <div class="font-medium">${i18n.t('program_selector.no_facilities_title')}</div>
          <div class="mt-1">
            ${i18n.t('program_selector.no_facilities_message')}
          </div>
        </div>
      </div>
    `;
  }

  householdChanged(event: Event) {
    const input = event.target as HTMLInputElement;
    const householdId = input.value.trim();

    if (householdId.length > 2) {
      this.showHouseholdInfo();
      this.householdMembersTarget.textContent = i18n.t('program_selector.household_id_info', { id: householdId });
    } else {
      this.hideHouseholdInfo();
    }
  }

  headOfHouseholdChanged(event: Event) {
    const checkbox = event.target as HTMLInputElement;

    if (checkbox.checked) {
      this.showHeadOfHouseholdInfo();
    } else {
      this.hideHeadOfHouseholdInfo();
    }
  }

  generateHouseholdId() {
    const timestamp = Date.now().toString(36).toUpperCase();
    const random = Math.random().toString(36).substr(2, 3).toUpperCase();
    const householdId = `HH_${timestamp}_${random}`;

    this.householdInputTarget.value = householdId;
    this.hideHouseholdInfo();
  }

  private showShelterFields() {
    this.shelterFieldsTarget.classList.remove("hidden");
  }

  private hideShelterFields() {
    this.shelterFieldsTarget.classList.add("hidden");
    this.hideHouseholdInfo();
    this.hideHeadOfHouseholdInfo();
    this.hideAutoExitInfo();
  }

  private showHouseholdInfo() {
    this.householdInfoTarget.classList.remove("hidden");
  }

  private hideHouseholdInfo() {
    this.householdInfoTarget.classList.add("hidden");
  }

  private showHeadOfHouseholdInfo() {
    this.headOfHouseholdInfoTarget.classList.remove("hidden");
  }

  private hideHeadOfHouseholdInfo() {
    this.headOfHouseholdInfoTarget.classList.add("hidden");
  }

  private showAutoExitInfo(autoExitDays: number) {
    this.autoExitInfoTarget.innerHTML = `
      <div class="alert alert-warning alert-sm">
        <div class="text-sm">
          <div class="font-medium">${i18n.t('program_selector.auto_exit_title')}</div>
          <div class="mt-1">
            ${i18n.t('program_selector.auto_exit_message', { days: autoExitDays })}
          </div>
        </div>
      </div>
    `;
    this.autoExitInfoTarget.classList.remove("hidden");
  }

  private hideAutoExitInfo() {
    this.autoExitInfoTarget.classList.add("hidden");
  }

  addProgram(event: Event) {
    event.preventDefault();
    const element = event.currentTarget as HTMLElement;
    const programId = element.dataset.programId;
    const programName = element.dataset.programName;
    const programFocus = element.dataset.programFocus;

    // Check if already added
    if (document.getElementById(`suggestion_${programId}`)) {
      return;
    }

    // Create the suggestion card HTML
    const suggestionHTML = `
      <div id="suggestion_${programId}" class="border rounded-lg p-3">
        <div class="flex justify-between items-start mb-2">
          <div class="font-medium">${programName}</div>
          <button
            type="button"
            class="btn btn-ghost btn-xs"
            data-action="click->program-selector#removeProgram"
            data-program-id="${programId}"
          >
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
        </div>
        <input type="hidden" name="programs[]" value="${programId}">
        <textarea
          name="comments[${programId}]"
          placeholder="${i18n.t('program_selector.add_comment_placeholder')}"
          class="textarea textarea-bordered w-full text-sm"
          rows="3"
        ></textarea>
      </div>
    `;

    // Add to selected programs
    const selectedPrograms = document.getElementById("selected_programs");
    if (selectedPrograms) {
      // Remove empty state message if present
      const emptyMessage = selectedPrograms.querySelector(".text-gray-400");
      if (emptyMessage) {
        emptyMessage.remove();
      }

      selectedPrograms.insertAdjacentHTML("beforeend", suggestionHTML);
    }
  }

  removeProgram(event: Event) {
    console.log("REMOVE PROGRAM called");
    event.preventDefault();
    const button = event.currentTarget as HTMLButtonElement;
    const programId = button.dataset.programId;

    // Alternative: just remove the closest parent card
    const card = button.closest(".border.rounded-lg");
    console.log("Found card via closest:", card);
    if (card) {
      card.remove();
      console.log("Removed card via closest");

      // Check if selected programs list is now empty
      const selectedPrograms = document.getElementById("selected_programs");
      if (selectedPrograms && selectedPrograms.children.length === 0) {
        selectedPrograms.innerHTML =
          `<p class="text-gray-400 text-center py-4">${i18n.t('program_selector.no_programs_selected')}</p>`;
      }
      return;
    }

    console.log("programId:", programId);
    console.log(
      "Looking for element with id:",
      `placements_program_${programId}_suggestion`,
    );

    // Find and remove the suggestion card (Rails dom_id generates placements_program_X_suggestion)
    const suggestionCard = document.getElementById(
      `placements_program_${programId}_suggestion`,
    );
    console.log("Found suggestion card:", suggestionCard);

    if (suggestionCard) {
      suggestionCard.remove();
      console.log("Removed suggestion card");
    } else {
      console.log("ERROR: Suggestion card not found!");
      console.log(
        "All elements with program_*_suggestion:",
        document.querySelectorAll('[id*="_suggestion"]'),
      );
    }

    // Check if selected programs list is now empty
    const selectedPrograms = document.getElementById("selected_programs");
    if (selectedPrograms && selectedPrograms.children.length === 0) {
      selectedPrograms.innerHTML =
        `<p class="text-gray-400 text-center py-4">${i18n.t('program_selector.no_programs_selected')}</p>`;
    }
  }
}
